import type { AdminAccountReportVO } from '../../types/admin/AdminAccountReportVO';
import type { KnowledgeAnswer, KnowledgeContext, KnowledgeLink, KnowledgeQuery } from './knowledgeRegistry';
import { breakdownRows, displayDate } from './lookupFormat';
import { claimsLookup, extractEmail } from './lookupRouting';
import { searchAdminAccounts } from './requests/account-lookup-requests';

/**
 * Finds the account behind an email address, for an administrator: "who is
 * jane@acme.com", "is jane@acme.com activated", "what account is
 * dispatch@acme.com".
 *
 * Support calls start with an email address far more often than with a company
 * name, and the first things to establish are whose account it is, what kind it
 * is and whether it is switched on. The admin account report has all three but
 * matches email partially, so only an exact match — on the account's email or
 * its username — is reported as the account; anything else is offered as a
 * near miss.
 */

/** Accounts listed when one email is on several (a buyer and a supplier, say). */
const MAX_LISTED = 3;

const sameAddress = (a?: string | null, b?: string | null): boolean =>
  Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());

const matchesExactly = (row: AdminAccountReportVO, email: string): boolean =>
  sameAddress(row.email, email) || sameAddress(row.username, email);

const typeLabel = (row: AdminAccountReportVO): string => row.userType?.trim() || 'Unknown type';

const statusLabel = (row: AdminAccountReportVO): string => (row.activated ? 'Activated' : 'Not activated');

const article = (word: string): string => (/^[aeiou]/i.test(word) ? 'an' : 'a');

/** "v3, 01/05/2024, by jane@acme.com" — whatever the proof carries. */
const agreement = (version?: number | null, date?: string | null, by?: string | null): string =>
  [version ? `v${version}` : '', displayDate(date), by ? `by ${by}` : ''].filter(Boolean).join(', ');

/**
 * What activation changes, where the code says so: a supplier account that is
 * not activated has every location hidden from marketplace search
 * (`LocationService.isLocationVisible`).
 */
const activationNote = (row: AdminAccountReportVO): string => {
  if (row.activated) return '';
  return /supplier/i.test(row.userType ?? '')
    ? ' Its locations are hidden from marketplace search until it is activated.'
    : '';
};

const accountsLink = (email: string): KnowledgeLink => ({
  label: 'Open Admin Accounts',
  path: `/admin-accounts?email=${encodeURIComponent(email)}`,
});

const singleAccount = (row: AdminAccountReportVO, email: string): KnowledgeAnswer => {
  const type = typeLabel(row);
  const created = displayDate(row.creationDate);
  const kind = row.activated
    ? `an activated **${type}** account`
    : `${article(type)} **${type}** account that is **not activated**`;
  return {
    text:
      `**${email}** belongs to **${row.companyName?.trim() || 'an unnamed company'}**, ${kind}` +
      `${created ? `, created ${created}` : ''}.${activationNote(row)}`,
    breakdown: breakdownRows([
      ['Company', row.companyName],
      ['Account type', type],
      ['Status', statusLabel(row)],
      ['Account email', row.email],
      ['Username', row.username],
      ['Customer code', row.customerCode],
      ['Created', created],
      ['Terms accepted', agreement(row.termsVersion, row.termsAgreedToDate, row.termsAgreedToEmail)],
      [
        'Privacy policy accepted',
        agreement(row.policyVersion, row.privacyPolicyAgreeToDate, row.privacyPolicyAgreedToEmail),
      ],
    ]),
    link: accountsLink(email),
  };
};

export const answerAdminAccountLookup = async ({ message }: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const email = extractEmail(message);
  if (!email) {
    return {
      text:
        'Which account do you mean? Give me the email address it signs in with \u2014 for example ' +
        '_"who is jane@acme.com"_ \u2014 and I will look it up.',
    };
  }

  // One account can have this as its email while another uses it as its username,
  // so both fields are searched and each account is counted once.
  const [byEmail, byUsername] = await Promise.all([
    searchAdminAccounts('email', email),
    searchAdminAccounts('username', email),
  ]);
  const partial = Array.from(new Map([...byEmail, ...byUsername].map((row) => [row.id, row])).values());
  const exact = partial.filter((row) => matchesExactly(row, email));

  if (exact.length === 1) return singleAccount(exact[0], email);

  if (exact.length > 1) {
    return {
      text:
        `**${exact.length} accounts** use **${email}** \u2014 the same person can hold a buyer and a supplier ` +
        'account.',
      breakdown: exact.slice(0, MAX_LISTED).map((row) => ({
        label: row.companyName?.trim() || 'Unnamed company',
        value: [
          typeLabel(row),
          statusLabel(row),
          row.customerCode?.trim() ? `customer code ${row.customerCode.trim()}` : '',
          displayDate(row.creationDate) ? `created ${displayDate(row.creationDate)}` : '',
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
      })),
      link: accountsLink(email),
    };
  }

  const near = Array.from(
    new Set(partial.map((row) => row.email?.trim()).filter((value): value is string => Boolean(value))),
  ).slice(0, MAX_LISTED);

  return {
    text:
      `No account signs in with **${email}**.` +
      (near.length
        ? ` Close matches: ${near.map((value) => `**${value}**`).join(', ')}.`
        : ' A team member (a dispatcher or gate clerk, say) signs in as their own user under ' +
          'their company\u2019s account, and those logins are not searchable here \u2014 look the ' +
          'company up in Admin Accounts instead.'),
    link: accountsLink(email),
  };
};

export const adminAccountLookupQuery: KnowledgeQuery = {
  id: 'adminAccountLookup',
  audiences: ['admin'],
  synonyms: [
    'look up an account by email',
    'find an account by email',
    'which account uses this email',
    'who owns this email',
  ],
  claims: claimsLookup('account'),
  needsLocations: false,
  example: 'who is jane@acme.com',
  description:
    'Finds the account behind an email address, for an administrator (e.g. "who is ' +
    'jane@acme.com", "is jane@acme.com activated", "what account is dispatch@acme.com"). ' +
    'Returns the company, whether it is a buyer or supplier account, whether it is activated, ' +
    'its customer code, when it was created and when it accepted the terms.',
  answer: answerAdminAccountLookup,
};

export const __testing = { matchesExactly };
